import { FC, useState } from 'react'
import classNames from 'classnames'
import { useNavigate, useParams } from 'react-router-dom'
import { Card } from 'src/components/Card'
import { SentimentDistributionCard } from 'src/components/SentimentDistributionCard'
import { StatusModal } from 'src/components/StatusModal'
import {
  REVIEW_ANALYSIS_CONNECT_ROUTE,
  REVIEW_ANALYSIS_FACILITY_DETAILS_ROUTE,
} from 'src/constants/routes'
import { useDeleteReviewAnalysisFacilityMutation } from 'src/reactQuery/useDeleteReviewAnalysisFacilityMutation'
import { useDeleteReviewAnalysisPlatformMutation } from 'src/reactQuery/useDeleteReviewAnalysisPlatformMutation'
import { useGetReviewAnalysisOverviewQuery } from 'src/reactQuery/useGetReviewAnalysisOverviewQuery'
import { useRenameReviewAnalysisFacilityMutation } from 'src/reactQuery/useRenameReviewAnalysisFacilityMutation'
import {
  useReviewAnalysisDateRange,
  useReviewAnalysisPeriodLabel,
  useReviewAnalysisLinkQuery,
} from 'src/hooks/useReviewAnalysisDateRange'
import { ReviewPlatform } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { createLink } from 'src/utils/createLink'
import classes from './brandLevelWidgets.module.scss'
import { FacilityAnalysisCard } from './FacilityAnalysisCard'
import { ReviewStatsCards } from './ReviewStatsCards'
import { ReviewTopicsAnalysis } from './ReviewTopicsAnalysis'
import { ReviewsByPlatform, ReviewsByPlatformView } from './ReviewsByPlatform'
import { TopBrandIssues } from './TopBrandIssues'
import { LoadingSkeleton } from '../LoadingSkeleton'
import { ReviewSmartInsights } from '../ReviewSmartInsights'
import { AddReviewPlatformModal } from '../AddReviewPlatformModal'
import { DeleteLocationModal } from '../DeleteLocationModal'
import { RenameFacilityModal } from '../RenameFacilityModal'
import { ReviewAnalysisWidget } from '../ReviewAnalysisWidget'

export const BrandLevelWidgets: FC = () => {
  const navigate = useNavigate()
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const [platformModalMode, setPlatformModalMode] = useState<'add' | 'edit'>(
    'add'
  )
  const [isAddPlatformModalOpen, setIsAddPlatformModalOpen] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    title: string
  } | null>(null)
  const [addModalFacility, setAddModalFacility] = useState<{
    id: string
    name: string
  } | null>(null)
  const [editPlatformTarget, setEditPlatformTarget] = useState<{
    cardId: string
    facilityId: string
    facilityName: string
    links: string[]
    platform: ReviewPlatform
    title: string
  } | null>(null)
  const [renameTarget, setRenameTarget] = useState<{
    category: string
    id: string
    name: string
  } | null>(null)
  const [platformView, setPlatformView] =
    useState<ReviewsByPlatformView>('reviews')
  const [deleteFacilityTarget, setDeleteFacilityTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  // A failed delete used to leave the confirm dialog open with no feedback,
  // which reads as "the button does nothing". Close it and say what happened.
  const [deleteError, setDeleteError] = useState<
    'facility' | 'platform' | null
  >(null)

  const dateRange = useReviewAnalysisDateRange()
  const periodLabel = useReviewAnalysisPeriodLabel()
  const linkQuery = useReviewAnalysisLinkQuery()
  const { data, isLoading } = useGetReviewAnalysisOverviewQuery(
    campaignId,
    dateRange
  )

  const deletePlatform = useDeleteReviewAnalysisPlatformMutation(campaignId, {
    onError: () => {
      setDeleteTarget(null)
      setDeleteError('platform')
    },
    onSuccess: () => setDeleteTarget(null),
  })
  const renameFacility = useRenameReviewAnalysisFacilityMutation(campaignId, {
    onSuccess: () => setRenameTarget(null),
  })
  const deleteFacility = useDeleteReviewAnalysisFacilityMutation(campaignId, {
    onError: () => {
      setDeleteFacilityTarget(null)
      setDeleteError('facility')
    },
    onSuccess: () => setDeleteFacilityTarget(null),
  })

  // Adding goes through the Connect Review Platforms page; the modal is
  // kept for editing one platform link.
  const openConnect = (facility?: { id: string; name: string }) =>
    navigate(
      createLink(REVIEW_ANALYSIS_CONNECT_ROUTE, {
        params: { campaignId },
        queryParams: {
          ...linkQuery,
          ...(facility
            ? { facilityId: facility.id, facilityName: facility.name }
            : {}),
        },
      })
    )
  const openAddPlatform = () => openConnect()

  if (isLoading || !data) {
    return <LoadingSkeleton />
  }

  const { facilities, reviewsByPlatform, stats, topIssues } = data

  return (
    <div className={classes.wrapper}>
      <ReviewSmartInsights />

      {facilities.length === 0 ? (
        <ReviewAnalysisWidget onAddPlatform={openAddPlatform} />
      ) : (
        <Card className={classes.reviewAnalysisCard}>
          <h2 className={classes.reviewAnalysisTitle}>Executive Overview</h2>
          {stats && (
            // Two columns: stats + sentiment over Reviews by Platform on the
            // left, Top Brand Issues down the full height on the right.
            <div className={classes.overviewGrid}>
              <div className={classes.overviewStats}>
                <ReviewStatsCards
                  averageRating={stats.averageRating}
                  averageRatingChange={stats.averageRatingChange}
                  combined
                  periodLabel={periodLabel}
                  totalReviews={stats.totalReviews}
                  totalReviewsChange={stats.totalReviewsChange}
                  trendLabel={dateRange ? 'vs previous period' : undefined}
                />
              </div>
              <SentimentDistributionCard
                className={classes.overviewSentiment}
                data={stats.sentiment}
              />
              <div className={classes.bottomCardLeft}>
                <div className={classes.bottomCardHeader}>
                  <h3 className={classes.bottomCardTitle}>
                    Reviews by Platform
                  </h3>
                  <div className={classes.viewToggle}>
                    <button
                      className={classNames(classes.viewToggleBtn, {
                        [classes.viewToggleBtnActive]:
                          platformView === 'reviews',
                      })}
                      onClick={() => setPlatformView('reviews')}
                      type="button"
                    >
                      Reviews
                    </button>
                    <button
                      className={classNames(classes.viewToggleBtn, {
                        [classes.viewToggleBtnActive]: platformView === 'chart',
                      })}
                      onClick={() => setPlatformView('chart')}
                      type="button"
                    >
                      Chart
                    </button>
                  </div>
                </div>
                <ReviewsByPlatform
                  onAddSource={openAddPlatform}
                  platforms={reviewsByPlatform}
                  view={platformView}
                />
              </div>
              <div className={classes.bottomCardRight}>
                <h3 className={classes.bottomCardTitle}>Top Brand Issues</h3>
                <TopBrandIssues issues={topIssues} />
              </div>
            </div>
          )}

          <div className={classes.topicsAnalysis}>
            <ReviewTopicsAnalysis campaignId={campaignId} variant="overview" />
          </div>

          <div className={classes.platformAnalysisSection}>
            <div className={classes.platformAnalysisHeader}>
              <h3 className={classes.platformAnalysisTitle}>
                Facility Analysis
              </h3>
              <button
                className={classes.addReviewBtn}
                onClick={openAddPlatform}
                type="button"
              >
                <span className={classes.addReviewBtnPlus}>+</span>
                Add Review Facility
              </button>
            </div>

            <div className={classes.facilityList}>
              {facilities.map((facility) => (
                <FacilityAnalysisCard
                  facility={facility}
                  key={facility.id}
                  onAddLocation={() =>
                    openConnect({ id: facility.id, name: facility.name })
                  }
                  onDelete={() =>
                    setDeleteFacilityTarget({
                      id: facility.id,
                      name: facility.name,
                    })
                  }
                  onDeleteCard={(card) =>
                    setDeleteTarget({ id: card.id, title: card.title })
                  }
                  onEditCard={(card) => {
                    setPlatformModalMode('edit')
                    setAddModalFacility({
                      id: facility.id,
                      name: facility.name,
                    })
                    setEditPlatformTarget({
                      cardId: card.id,
                      facilityId: facility.id,
                      facilityName: facility.name,
                      links: card.links,
                      platform: card.platform,
                      title: card.title,
                    })
                    setIsAddPlatformModalOpen(true)
                  }}
                  onRename={() =>
                    setRenameTarget({
                      category: facility.category ?? '',
                      id: facility.id,
                      name: facility.name,
                    })
                  }
                  // One location across its platforms: the facility page
                  // scoped to that venue, on All Platforms.
                  onViewBranch={(title) =>
                    navigate(
                      createLink(REVIEW_ANALYSIS_FACILITY_DETAILS_ROUTE, {
                        params: { campaignId, facilityId: facility.id },
                        queryParams: { ...linkQuery, venue: title },
                      })
                    )
                  }
                  onViewFacility={() =>
                    navigate(
                      createLink(REVIEW_ANALYSIS_FACILITY_DETAILS_ROUTE, {
                        params: { campaignId, facilityId: facility.id },
                        queryParams: linkQuery,
                      })
                    )
                  }
                  periodLabel={periodLabel}
                />
              ))}
            </div>
          </div>
        </Card>
      )}
      <AddReviewPlatformModal
        initialCardId={editPlatformTarget?.cardId}
        initialFacilityId={addModalFacility?.id}
        initialFacilityName={addModalFacility?.name}
        initialLinks={editPlatformTarget?.links}
        initialPlatform={editPlatformTarget?.platform}
        initialTitle={editPlatformTarget?.title}
        isOpen={isAddPlatformModalOpen}
        lockFacility={!!addModalFacility}
        mode={platformModalMode}
        onClose={() => {
          setIsAddPlatformModalOpen(false)
          setAddModalFacility(null)
          setEditPlatformTarget(null)
        }}
        onError={() => {
          setIsAddPlatformModalOpen(false)
          setEditPlatformTarget(null)
          setIsErrorModalOpen(true)
        }}
        onSuccess={() => {
          setIsAddPlatformModalOpen(false)
          setEditPlatformTarget(null)
          setIsSuccessModalOpen(true)
        }}
      />
      <StatusModal
        hideCountdown
        isOpen={isSuccessModalOpen}
        message={
          platformModalMode === 'edit'
            ? 'Your review platform was updated successfully.'
            : "Everything is set. We'll begin monitoring this location and notify you as soon as something important happens."
        }
        onClose={() => setIsSuccessModalOpen(false)}
        title={
          platformModalMode === 'edit'
            ? 'Review Platform Updated!'
            : 'Location Added Successfully!'
        }
        type="success"
      />
      <StatusModal
        doneLabel="Try Again"
        hideCountdown
        isOpen={isErrorModalOpen}
        message={
          platformModalMode === 'edit'
            ? 'Something went wrong while saving your changes. Please try again in a moment.'
            : 'Something went wrong while adding the location. Please try again in a moment.'
        }
        onClose={() => setIsErrorModalOpen(false)}
        onSecondaryAction={() => {
          setIsErrorModalOpen(false)
          setIsAddPlatformModalOpen(true)
        }}
        secondaryLabel="Discard"
        title={
          platformModalMode === 'edit'
            ? "Review Platform Couldn't Be Updated"
            : "Location Couldn't Be Added"
        }
        type="error"
      />
      <DeleteLocationModal
        isOpen={!!deleteTarget}
        isPending={deletePlatform.isPending}
        locationName={deleteTarget?.title ?? ''}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deletePlatform.mutate(deleteTarget.id)
        }}
      />
      <DeleteLocationModal
        isOpen={!!deleteFacilityTarget}
        isPending={deleteFacility.isPending}
        locationName={deleteFacilityTarget?.name ?? ''}
        onClose={() => setDeleteFacilityTarget(null)}
        onConfirm={() => {
          if (deleteFacilityTarget)
            deleteFacility.mutate(deleteFacilityTarget.id)
        }}
      />
      <StatusModal
        hideCountdown
        isOpen={deleteError !== null}
        message={
          deleteError === 'facility'
            ? 'Something went wrong while deleting the facility. Please try again in a moment.'
            : 'Something went wrong while deleting the location. Please try again in a moment.'
        }
        onClose={() => setDeleteError(null)}
        title={
          deleteError === 'facility'
            ? "Facility Couldn't Be Deleted"
            : "Location Couldn't Be Deleted"
        }
        type="error"
      />
      <RenameFacilityModal
        currentCategory={renameTarget?.category ?? ''}
        currentName={renameTarget?.name ?? ''}
        isOpen={!!renameTarget}
        onClose={() => setRenameTarget(null)}
        onConfirm={({ category, name }) => {
          if (renameTarget)
            renameFacility.mutate({
              facilityId: renameTarget.id,
              ...(name !== undefined ? { name } : {}),
              // '' (picked again to unselect) clears it
              ...(category !== undefined ? { category: category || null } : {}),
            })
        }}
      />
    </div>
  )
}
